import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Badge, Button, Group, Modal, Paper, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBookmark, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

// Saved custom reports: the definition stores the report tab + branch +
// date range. Clicking a chip re-applies all three; execution then
// happens through the normal report queries — the server never runs
// user-defined queries.

interface SavedReportDto {
  savedReportId: string;
  nameAr: string;
  definitionJson: string;
}

export interface ReportDefinition {
  tab: string;
  branchId: string | null;
  fromMs: number;
  toMs: number;
}

interface Props {
  current: { tab: string; branchId: string | null; range: [Date | null, Date | null] };
  onApply: (def: ReportDefinition) => void;
}

export function SavedReportsBar({ current, onApply }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const { data: saved } = useQuery({
    queryKey: ['saved-reports'] as const,
    queryFn: async () => (await apiClient.get<SavedReportDto[]>('/api/reports/saved')).data,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['saved-reports'] });

  const create = useMutation({
    mutationFn: async () => {
      const def: ReportDefinition = {
        tab: current.tab,
        branchId: current.branchId,
        fromMs: current.range[0]?.getTime() ?? Date.now(),
        toMs: current.range[1]?.getTime() ?? Date.now(),
      };
      return apiClient.post('/api/reports/saved', {
        nameAr: name,
        definitionJson: JSON.stringify(def),
      });
    },
    onSuccess: () => {
      invalidate();
      setSaveOpen(false);
      setName('');
      notifications.show({ color: 'green', title: t('reports.saved.savedToast'), message: '' });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/api/reports/saved/${id}`),
    onSuccess: invalidate,
  });

  const apply = (report: SavedReportDto) => {
    try {
      const def = JSON.parse(report.definitionJson) as Partial<ReportDefinition>;
      if (typeof def.tab === 'string' && typeof def.fromMs === 'number' && typeof def.toMs === 'number') {
        onApply({
          tab: def.tab,
          branchId: typeof def.branchId === 'string' ? def.branchId : null,
          fromMs: def.fromMs,
          toMs: def.toMs,
        });
      }
    } catch {
      // Corrupt definition — ignore rather than crash the page.
    }
  };

  return (
    <Paper p="sm" withBorder>
      <Group gap="xs" wrap="wrap">
        <Text size="sm" fw={600} c="var(--wheels-ink-strong)">
          {t('reports.saved.title')}
        </Text>
        {(saved ?? []).length === 0 && (
          <Text size="sm" c="dimmed">
            {t('reports.saved.empty')}
          </Text>
        )}
        {(saved ?? []).map((report) => (
          <Badge
            key={report.savedReportId}
            variant="light"
            color="wheels"
            radius="sm"
            size="lg"
            className="cursor-pointer"
            onClick={() => apply(report)}
            rightSection={
              <ActionIcon
                variant="transparent"
                color="gray"
                size={14}
                aria-label={t('common.delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  remove.mutate(report.savedReportId);
                }}
              >
                <IconX size={12} stroke={1.8} />
              </ActionIcon>
            }
          >
            {report.nameAr}
          </Badge>
        ))}
        <Button
          size="xs"
          variant="default"
          leftSection={<IconBookmark size={14} stroke={1.6} />}
          onClick={() => setSaveOpen(true)}
        >
          {t('reports.saved.saveCurrent')}
        </Button>
      </Group>

      <Modal opened={saveOpen} onClose={() => setSaveOpen(false)} title={t('reports.saved.saveCurrent')}>
        <TextInput
          label={t('reports.saved.nameLabel')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          data-autofocus
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setSaveOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            loading={create.isPending}
            disabled={!name.trim()}
            onClick={() => create.mutate()}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Modal>
    </Paper>
  );
}
